import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { bindActionCreators, compose } from 'redux';

import * as D from '../../deps';
import { isBackstageRightRailActive } from '../../utils';
import BackstageHeroSidebarImage from '../BackstageHeroSidebarImage';
import BackstageLongPlayButton from '../BackstageLongPlayButton';

import BackstageLargeHeaderStickySidebar from './BackstageLargeHeaderStickySidebar';

import './BackstageLargeHeaderLayout.scss';

const HeroImageWithLogger = D.withVisibilityLogger({
  contentSection: D.Constants.ContentSections.BackstageContentSections.HERO_IMAGE,
})(D.Img);

export class BackstageLargeHeaderLayout extends React.Component {
  static propTypes = {
    renderStructuredMetaData: PropTypes.func,
    headerBackstage: PropTypes.node,
    heroImageUrl: PropTypes.string,
    heroImageLinkUrl: PropTypes.string,
    heroImageIsCircle: PropTypes.bool,
    renderSidebarButtons: PropTypes.func,
    renderMainContent: PropTypes.func.isRequired,
    showedBackstagePage: PropTypes.func.isRequired,
    // The `entityGetterFunc` will be called with `entityIdentifier` to retrieve data to render.
    // Original backstage pages used an seoToken parsed from the URL. Newer pages use the pandoraId associated with an entity
    entityIdentifier: PropTypes.string,
    entityGetterFunc: PropTypes.func,
    pandoraId: PropTypes.string,
    currentPathname: PropTypes.string,
    isRightRailActive: PropTypes.bool,
    isCuratorExperienceEnabled: PropTypes.bool,
    renderArtworkPlayButton: PropTypes.bool,
    playOnClick: PropTypes.func,
    mobileLongLink: PropTypes.string,
    largeHeader: PropTypes.node,
    dropToNextLine: PropTypes.bool,
    artistBio: PropTypes.node,
  };

  componentDidMount() {
    this.fetchData();
    this.logViewed();
  }

  componentDidUpdate(prevProps) {
    if (
      D.isBackstageParentPath(this.props.currentPathname) &&
      this.props.entityIdentifier !== prevProps.entityIdentifier
    ) {
      D.Utils.Scroll.scrollToTop();
      this.fetchData();
    }
    if (
      (this.props.currentPathname !== prevProps.currentPathname &&
        D.isBackstageChildPath(this.props.currentPathname)) ||
      this.props.entityIdentifier !== prevProps.entityIdentifier
    ) {
      this.logViewed();
    }
  }

  fetchData = () => {
    const { entityIdentifier, entityGetterFunc } = this.props;
    if (entityIdentifier && entityGetterFunc) {
      entityGetterFunc(entityIdentifier);
    }
  };

  logViewed = () => {
    if (this.props.entityIdentifier) {
      this.props.showedBackstagePage({ pageId: this.props.entityIdentifier });
    }
  };

  render() {
    const {
      isRightRailActive,
      isCuratorExperienceEnabled,
      renderArtworkPlayButton,
      dropToNextLine,
      renderStructuredMetaData,
      mobileLongLink,
      largeHeader,
      headerBackstage,
      heroImageIsCircle,
      heroImageLinkUrl,
      heroImageUrl,
      playOnClick,
      renderSidebarButtons,
      currentPathname,
      artistBio,
      renderMainContent,
    } = this.props;

    const classes = D.createBEM('BackstageLargeHeaderLayout', {
      dropToNextLine,
      rightRail: isRightRailActive,
      hasHeaderLogo: renderArtworkPlayButton,
      fullBio: D.isBackstageArtistFullBioPath(currentPathname),
    });

    return (
      <div className={classes} data-qa={D.QA.BS}>
        {renderStructuredMetaData && renderStructuredMetaData()}
        {renderArtworkPlayButton ? (
          <a href={mobileLongLink} className='BackstageLargeHeaderLayout__logo'>
            <D.Icon className='BackstageLargeHeaderLayout__logo__icon' glyph={D.LogoPandoraIcon} />
          </a>
        ) : (
          largeHeader
        )}
        <div className='BackstageLargeHeaderLayout__body'>
          <header className='BackstageLargeHeaderLayout__body__header'>{headerBackstage}</header>
          <aside className='BackstageLargeHeaderLayout__body__aside'>
            <BackstageLargeHeaderStickySidebar>
              <BackstageHeroSidebarImage
                heroImageIsCircle={heroImageIsCircle}
                isCuratorExperienceEnabled={isCuratorExperienceEnabled}
                isRightRailActive={isRightRailActive}
                renderArtworkPlayButton={renderArtworkPlayButton}
              >
                <Link to={heroImageLinkUrl}>
                  <HeroImageWithLogger
                    src={heroImageUrl}
                    isCircle={heroImageIsCircle}
                    useDefaultBgColor={true}
                  />
                  {renderArtworkPlayButton && (
                    <BackstageLongPlayButton
                      playOnClick={playOnClick}
                      isArtist={heroImageIsCircle}
                    />
                  )}
                </Link>
              </BackstageHeroSidebarImage>
              {renderSidebarButtons && renderSidebarButtons()}
              {!D.isBackstageArtistFullBioPath(currentPathname) && artistBio}
            </BackstageLargeHeaderStickySidebar>
          </aside>
          <main className='BackstageLargeHeaderLayout__body__main'>
            {renderMainContent && renderMainContent()}
          </main>
        </div>
        <D.Footer useLightTheme={true} />
      </div>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  return {
    currentPathname: D.getCurrentPathnameFromState(state),
    isCuratorExperienceEnabled: D.Utils.TunerVars.curatorExperienceEnabled(state),
    isRightRailActive: isBackstageRightRailActive(state),
    dropToNextLine: ownProps.shouldDropSearchToNextLine && !ownProps.renderArtworkPlayButton,
    mobileLongLink: D.getMobileLongLink(state, {
      pageUrl: D.getCurrentPathnameFromState(state),
      feature: D.Constants.Urls.deepLinkParams.BACKSTAGE_PAGE,
    }),
  };
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      showedBackstagePage: D.getInDomain('backstage.actions.showedBackstagePage', noop),
    },
    dispatch,
  );
};

const enhance = compose(
  D.BgSetter,
  D.determineShouldDropSearchToNextLine,
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(BackstageLargeHeaderLayout);
